import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Loader2, 
  X,
  ImagePlus,
  AlertCircle 
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Support ticket categories
const ticketCategories = [
  { value: "profile", label: "Profile" },
  { value: "account", label: "Account" },
  { value: "coin_purchase", label: "Coin Purchase" },
  { value: "gameplay", label: "Gameplay" },
  { value: "bugs", label: "Bugs" },
  { value: "other", label: "Other" },
];

// Maximum image file size (2MB)
const MAX_FILE_SIZE = 2 * 1024 * 1024;
// Accepted image MIME types
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

// Form validation schema
const formSchema = z.object({
  subject: z.string()
    .min(5, { message: 'Subject must be at least 5 characters' })
    .max(100, { message: 'Subject cannot exceed 100 characters' }),
  description: z.string()
    .min(10, { message: 'Description must be at least 10 characters' })
    .max(1000, { message: 'Description cannot exceed 1000 characters' }),
  category: z.enum(["profile", "account", "coin_purchase", "gameplay", "bugs", "other"]),
  priority: z.enum(['low', 'medium', 'high']).default('medium'),
  // Images are handled separately through the file input
  // We'll validate files through the upload handler
});

type FormValues = z.infer<typeof formSchema>;

export default function NewTicketForm() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [images, setImages] = useState<File[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);
  
  // Initialize form with react-hook-form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      subject: '',
      description: '',
      category: 'other',
      priority: 'medium',
    },
  });
  
  // Handle image file uploads
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImageError(null);
    
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    const file = event.target.files[0];
    
    // Validate file type
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageError("Invalid file type. Only JPEG, PNG and WebP images are allowed.");
      event.target.value = '';
      return;
    }
    
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setImageError("File size too large. Maximum size is 2MB.");
      event.target.value = '';
      return;
    }
    
    // Check if we already have 3 images
    if (images.length >= 3) {
      setImageError("Maximum of 3 images allowed.");
      event.target.value = '';
      return;
    }
    
    // Add the new image to our list
    setImages([...images, file]);
    
    // Reset the input field
    event.target.value = '';
  };
  
  // Remove an image from the list
  const removeImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
    setImageError(null);
  };
  
  // Mutation for creating a new ticket
  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      // Create FormData to handle file uploads
      const formData = new FormData();
      
      // Append form field data
      formData.append('subject', data.subject);
      formData.append('description', data.description);
      formData.append('category', data.category);
      formData.append('priority', data.priority);
      
      // Append images if any
      images.forEach((image, index) => {
        formData.append(`image${index}`, image);
      });
      
      const response = await apiRequest('POST', '/api/support/tickets', formData, true); // Set the last param to true to indicate FormData
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create support ticket');
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Support ticket created',
        description: 'Your ticket has been submitted successfully. We will respond as soon as possible.',
      });
      
      // Reset form
      form.reset();
      setImages([]);
      
      // Invalidate tickets query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['/api/support/tickets'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error creating ticket',
        description: error.message || 'An error occurred. Please try again.',
        variant: 'destructive',
      });
    },
  });
  
  // Form submission handler
  const onSubmit = (data: FormValues) => {
    mutation.mutate(data);
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-3">
        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-300 text-sm">Subject</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Brief summary of your issue" 
                  className="bg-zinc-800 border-zinc-700 text-white focus-visible:ring-green-500 focus-visible:ring-opacity-50 placeholder:text-gray-500"
                  {...field} 
                />
              </FormControl>
              <FormMessage className="text-red-400 text-xs" />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-gray-300 text-sm">Category</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white focus:ring-green-500 focus:ring-opacity-50">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                    {ticketCategories.map(category => (
                      <SelectItem key={category.value} value={category.value} className="focus:bg-zinc-700 focus:text-white">
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage className="text-red-400 text-xs" />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-gray-300 text-sm">Priority</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white focus:ring-green-500 focus:ring-opacity-50">
                      <SelectValue placeholder="Select a priority" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectItem value="low" className="focus:bg-zinc-700 focus:text-white">Low</SelectItem>
                    <SelectItem value="medium" className="focus:bg-zinc-700 focus:text-white">Medium</SelectItem>
                    <SelectItem value="high" className="focus:bg-zinc-700 focus:text-white">High</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="text-red-400 text-xs" />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-300 text-sm">Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Please describe your issue in detail..." 
                  className="min-h-[150px] bg-zinc-800 border-zinc-700 text-white focus-visible:ring-green-500 focus-visible:ring-opacity-50 placeholder:text-gray-500 resize-none"
                  {...field} 
                />
              </FormControl>
              <FormMessage className="text-red-400 text-xs" />
            </FormItem>
          )}
        />
        
        {/* Image upload section */}
        <div className="space-y-2 mt-2">
          <FormLabel className="text-gray-300 text-sm">Images (Optional)</FormLabel>
          <div className="flex flex-col space-y-2">
            <div className="flex flex-wrap gap-4 mb-2">
              {images.map((image, index) => (
                <div key={index} className="relative group">
                  <div className="w-20 h-20 rounded-md overflow-hidden bg-zinc-800 border border-zinc-700">
                    <img 
                      src={URL.createObjectURL(image)}
                      alt={`Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 w-6 h-6 flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {images.length < 3 && (
                <label className="w-20 h-20 flex items-center justify-center border border-dashed border-zinc-600 bg-zinc-800 rounded-md cursor-pointer hover:bg-zinc-700 transition-colors">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="sr-only"
                    onChange={handleImageUpload}
                  />
                  <ImagePlus className="w-6 h-6 text-gray-400" />
                </label>
              )}
            </div>
            
            <p className="text-xs text-gray-400">
              Upload up to 3 images (JPEG, PNG, WebP, max 2MB each)
            </p>
            
            {imageError && (
              <Alert variant="destructive" className="py-2 bg-red-900/30 border border-red-500/50 text-white">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-sm">{imageError}</AlertDescription>
              </Alert>
            )}
          </div>
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-green-600 hover:bg-green-700 text-white mt-4"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit Ticket'
          )}
        </Button>
      </form>
    </Form>
  );
}